/**
 * NOTE: This code belongs to Kangaroo Rewards. Unauthorized use is prohibited
 */

@file:Suppress("unused", "MemberVisibilityCanBePrivate", "EXPERIMENTAL_API_USAGE")
@file:OptIn(ExperimentalJsExport::class)
package features.pushTokenRegistration

import kangaroorewards.appsdk.core.api.Api
import kangaroorewards.appsdk.core.domain.Result
import kangaroorewards.appsdk.core.domain.Result.Idle
import kangaroorewards.appsdk.core.domain.Result.Loading
import kangaroorewards.appsdk.core.domain.SerializedResult
import kangaroorewards.appsdk.core.domain.toJsonResult
import kangaroorewards.appsdk.core.utils.CFlow
import kangaroorewards.appsdk.core.utils.asCFlow
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.mapLatest
import kotlinx.serialization.InternalSerializationApi
import kotlin.js.ExperimentalJsExport
import features.pushTokenRegistration.internal.PushTokenRegistrationUseCase
import features.pushTokenRegistration.internal.PushTokenRegistrationUseCase.Params
import features.pushTokenRegistration.models.UserProfileModel


typealias PushTokenRegistrationState = CFlow<PushTokenRegistrationUseCaseResult?>

typealias PushTokenRegistrationUseCaseResult = Result<UserProfileModel>

fun PushTokenRegistrationUseCaseResult.serializePushTokenRegistrationApiResult(): SerializedResult<String> {
    return this.toJsonResult<UserProfileModel>()
}

/**
 * Serializes [PushTokenRegistrationState]'s data and returns a new
 * [CFlow] containing data serialized as a JSON [String].
 */
@OptIn(ExperimentalCoroutinesApi::class)
fun PushTokenRegistrationState.serializePushTokenRegistrationState(): CFlow<SerializedResult<String>?> {
    return this.mapLatest {
        it?.toJsonResult<UserProfileModel>()
    }.asCFlow()
}

/**
 * Send user Push notification token [Api] class
 */
class PushTokenRegistrationApi : Api() {

    val pushTokenRegistrationState: PushTokenRegistrationState
        get() = _pushTokenRegistrationState.asCFlow()

    companion object {
        private val _pushTokenRegistrationState: MutableStateFlow<PushTokenRegistrationUseCaseResult> = MutableStateFlow(Idle())
    }

    private val pushTokenRegistrationUseCase = PushTokenRegistrationUseCase()

    /**
     * Send user Push notification token
     */
    suspend fun registerPushToken(
        overrideHeaders: Map<String, String>? = null,
        androidDeviceToken: String? = null,
        iosDeviceToken: String? = null,
        webDeviceToken: String? = null
    ): Result<UserProfileModel> {
        /* set state to Loading as soon as function is called */
        _pushTokenRegistrationState.value = Loading()
        val result = this.pushTokenRegistrationUseCase(
            Params(
                overrideHeaders = overrideHeaders,
                androidDeviceToken = androidDeviceToken,
                iosDeviceToken = iosDeviceToken,
                webDeviceToken = webDeviceToken
            )
        ) { result ->
            _pushTokenRegistrationState.value = result
            return@pushTokenRegistrationUseCase result
        }
        return result
    }

}
